/**
 * Simplified template-to-segments builder for modal preview.
 * Uses shared logic with TranslationGenerator where applicable.
 * Builds StepTranslation objects from AddStepFormState for live preview.
 */

import type { AddStepFormState } from './types';
import type { TransferFormState } from '@/graph-store/actions/transferHelpers';
import type {
  MaterialNodeSpec,
  OperationTypeDefinition,
  ParameterDefinition,
  Checkpoint,
} from '@/graph/domain/types';
import type {
  CheckpointDef,
  StepTranslation,
  TableColumn,
  TableRow,
  TranslationSegment,
} from '@/components/editor/RightPanel/TranslationPanel/translation.types';
import { buildScalarParamSegments, isRequired } from '@/components/editor/RightPanel/TranslationPanel/buildParamSegment';
import {
  normalizeTableColumns,
  pickGroupRows,
  pickRowArray,
  rowHasValue,
} from '@/components/editor/RightPanel/TranslationPanel/translationParamUtils';

// ─── Shared Helpers ────────────────────────────────────────────────────────

function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return false;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (('parameterId' in obj || 'parameterName' in obj) && 'value' in obj) {
      return isEmpty(obj.value);
    }
    if ('amount' in obj) return obj.amount == null || String(obj.amount).trim() === '';
    if ('value' in obj) {
      const v = obj.value;
      return v == null || (typeof v === 'string' && v.trim() === '');
    }
    return Object.values(obj).every((v) => isEmpty(v));
  }
  return false;
}

function getValue(raw: unknown): string | number {
  if (raw == null) return '';
  if (typeof raw === 'number') return Number.isNaN(raw) ? '' : raw;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (obj.label != null && obj.label !== '') return String(obj.label);
    if (obj.value != null && obj.value !== '') return getValue(obj.value);
    if (obj.amount != null && obj.amount !== '') return getValue(obj.amount);
    if (obj.input != null && obj.input !== '') return getValue(obj.input);
    if (obj.label != null && obj.label !== '') return String(obj.label);
  }
  return '';
}

function append(segments: TranslationSegment[], text: string): void {
  if (!text) return;
  const last = segments[segments.length - 1];
  if (last?.kind === 'text') {
    last.value += text;
  } else {
    segments.push({ kind: 'text', value: text });
  }
}

function removeDanglingDurationPrefix(segments: TranslationSegment[]): void {
  const last = segments[segments.length - 1];
  if (!last || last.kind !== 'text') return;
  // Remove trailing "for" that can be left by templates like "... for {duration}".
  last.value = last.value.replace(/\s+for\s*$/i, '');
}

function formatDuration(totalSeconds: number): string {
  const safe = Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.floor(totalSeconds) : 0;
  if (safe === 0) return '0 sec';
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  return `${h}h ${m}m ${s}s`;
}

function buildPreviewDurationText(
  operationType: OperationTypeDefinition,
  totalSeconds: number,
): string | undefined {
  if (operationType.durationAllow === false) return undefined;
  const safe = Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.floor(totalSeconds) : 0;
  // Non-mandatory duration stays hidden when untouched at 0.
  if (operationType.durationRequired !== true && safe === 0) return undefined;
  return formatDuration(safe);
}

interface TransferPreviewOptions {
  transferForm?: TransferFormState | null;
  sourceMaterialId?: string;
  complexAdditionalInputIds?: string[];
  materialNodes?: Record<string, MaterialNodeSpec>;
}

function emptyTransferForm(): TransferFormState {
  return {
    quantityType: 'Volume',
    transferEntireVolume: false,
    sameVolume: false,
    rows: [],
    notes: '',
    recommendedInstrument: [],
    durationHours: 0,
    durationMinutes: 0,
    durationSeconds: 0,
    linkToPreviousStep: false,
    linkedStepId: null,
    checkpoints: [],
  };
}

function formatConstituents(material?: MaterialNodeSpec): string {
  if (!material?.constituents?.length) return '';
  return material.constituents
    .map((c) => {
      const pct = Number.isFinite(c.weight) ? Math.round(c.weight * 100) : null;
      return pct != null ? `${c.name}: ${pct}%` : c.name;
    })
    .join(', ');
}

function resolveContainerName(material?: MaterialNodeSpec): string {
  if (!material?.container) return '';
  const direct = material.container.name?.trim();
  if (direct) return direct;
  return material.container.containerType ?? '';
}

function buildTransferPreviewSegments(
  transferForm: TransferFormState,
  sourceMaterialId: string | undefined,
  materialNodes: Record<string, MaterialNodeSpec>,
): TranslationSegment[] {
  const segments: TranslationSegment[] = [];
  const row = transferForm.rows?.[0];

  segments.push({ kind: 'text', value: 'Transfer ' });

  if (row?.mode === 'numeric' && row.amount != null) {
    segments.push({
      kind: 'field',
      paramName: 'transferQuantity',
      value: row.amount,
      unit: row.unit ?? undefined,
      fieldType: 'number',
      disabled: true,
    });
    segments.push({ kind: 'text', value: row.unit ? ` ${row.unit} ` : ' ' });
  } else if (row?.mode === 'symbolic' && row.symbolic) {
    segments.push({ kind: 'text', value: `${row.symbolic} ` });
  } else {
    segments.push({
      kind: 'field',
      paramName: 'transferQuantity',
      value: '',
      fieldType: 'number',
      disabled: true,
    });
    segments.push({ kind: 'text', value: ' ' });
  }

  segments.push({ kind: 'text', value: 'of ' });

  const source = sourceMaterialId ? materialNodes[sourceMaterialId] : undefined;
  if (source) {
    segments.push({
      kind: 'material-ref',
      materialName: source.name,
      containerName: resolveContainerName(source),
      constituentExpr: formatConstituents(source),
      constituents: source.constituents || [],
    });
  }

  segments.push({ kind: 'text', value: ' to ' });

  const destination = row?.destinationMaterialId
    ? materialNodes[row.destinationMaterialId]
    : undefined;

  if (destination) {
    segments.push({
      kind: 'material-ref',
      materialName: destination.name,
      containerName: resolveContainerName(destination),
      constituentExpr: formatConstituents(destination),
      constituents: destination.constituents || [],
    });
  } else if (row?.destinationDraft) {
    segments.push({
      kind: 'material-ref',
      materialName: row.destinationDraft.name,
      containerName: row.destinationDraft.container?.name ?? '',
      constituentExpr: '',
      constituents: [],
    });
  }

  segments.push({ kind: 'text', value: '.' });
  return segments;
}

function removeTrailingPeriod(segments: TranslationSegment[]): void {
  const last = segments[segments.length - 1];
  if (!last || last.kind !== 'text') return;
  last.value = last.value.replace(/\s*\.\s*$/, '');
}

function ensureTrailingPeriod(segments: TranslationSegment[]): void {
  const last = segments[segments.length - 1];
  if (last?.kind === 'text') {
    if (!last.value.trim().endsWith('.')) {
      last.value += '.';
    }
    return;
  }
  segments.push({ kind: 'text', value: '.' });
}

function buildComplexPreviewSegments(
  operationType: OperationTypeDefinition,
  form: AddStepFormState,
  materialName: string,
  operatorName: string,
  options?: TransferPreviewOptions,
): TranslationSegment[] {
  const template = operationType.translationTemplate || '';
  const segments: TranslationSegment[] = [];
  const durationSec = form.durationHours * 3600 + form.durationMinutes * 60 + form.durationSeconds;
  const durationText = buildPreviewDurationText(operationType, durationSec);
  const paramSegs = buildParameterSegments(operationType, form);

  const sourceMaterial = options?.sourceMaterialId
    ? options.materialNodes?.[options.sourceMaterialId]
    : undefined;

  const tokens = template.split(/(\{operatorName\}|\{materialName\}|\{parameters\}|\{duration\})/gi);
  for (const token of tokens) {
    const lower = token.toLowerCase();

    if (lower === '{operatorname}') {
      append(segments, operatorName || operationType.operationName || 'Operator');
    } else if (lower === '{materialname}') {
      if (sourceMaterial) {
        segments.push({
          kind: 'material-ref',
          materialName: sourceMaterial.name,
          containerName: resolveContainerName(sourceMaterial),
          constituentExpr: formatConstituents(sourceMaterial),
          constituents: sourceMaterial.constituents || [],
        });
      } else {
        append(segments, materialName || 'Material');
      }
    } else if (lower === '{parameters}') {
      segments.push(...paramSegs);
    } else if (lower === '{duration}') {
      if (durationText) {
        append(segments, durationText);
      } else {
        removeDanglingDurationPrefix(segments);
      }
    } else {
      append(segments, token);
    }
  }

  if (segments.length === 0) {
    append(segments, `${operationType.operationName || 'Operation'} on ${materialName || 'Material'}`);
    if (paramSegs.length > 0) {
      append(segments, ' ');
      segments.push(...paramSegs);
    }
    if (durationText) {
      append(segments, ` for ${durationText}`);
    }
  }

  const auxIds = options?.complexAdditionalInputIds ?? [];
  const auxMaterials = auxIds
    .map((id) => options?.materialNodes?.[id])
    .filter((m): m is MaterialNodeSpec => Boolean(m));

  if (auxMaterials.length > 0) {
    removeTrailingPeriod(segments);
    segments.push({ kind: 'text', value: auxMaterials.length > 1 ? ' with auxiliary materials ' : ' with auxiliary material ' });
    auxMaterials.forEach((mat, idx) => {
      if (idx > 0) segments.push({ kind: 'text', value: ', ' });
      segments.push({
        kind: 'material-ref',
        materialName: mat.name,
        containerName: resolveContainerName(mat),
        constituentExpr: formatConstituents(mat),
        constituents: mat.constituents || [],
      });
    });
  }

  ensureTrailingPeriod(segments);
  return segments;
}

// ─── Build Segments ────────────────────────────────────────────────────────

export function buildPreviewStepTranslation(
  operationType: OperationTypeDefinition,
  form: AddStepFormState,
  materialName: string,
  operatorName: string,
  options?: TransferPreviewOptions,
): StepTranslation {
  if (operationType.operationType === 'TRANSFER') {
    const transferForm = options?.transferForm ?? emptyTransferForm();
    const segments = buildTransferPreviewSegments(
      transferForm,
      options?.sourceMaterialId,
      options?.materialNodes ?? {},
    );
    const checkpoints = (transferForm.checkpoints?.length ? transferForm.checkpoints : operationType.checkpoints || [])
      .map(mapCheckpoint);

    return {
      nodeId: 'preview-step',
      creationOrder: 0,
      stepNumber: '1',
      type: 'operation',
      operationName: operationType.operationName,
      archetype: operationType.operationType || 'TRANSFER',
      segments,
      checkpoints,
      icons: {
        hasNotes: Boolean(transferForm.notes?.trim()),
        hasApproval: false,
        hasNotifications: false,
      },
      notes: transferForm.notes,
      approvers: { approverType: null, users: [] },
      notifications: { notificationType: null, users: [] },
    };
  }

  if (operationType.operationType === 'COMPLEX') {
    const segments = buildComplexPreviewSegments(
      operationType,
      form,
      materialName,
      operatorName,
      options,
    );
    const checkpoints = (form.checkpoints?.length ? form.checkpoints : operationType.checkpoints || [])
      .map(mapCheckpoint);

    return {
      nodeId: 'preview-step',
      creationOrder: 0,
      stepNumber: '1',
      type: 'operation',
      operationName: operationType.operationName,
      archetype: 'COMPLEX',
      segments,
      checkpoints,
      icons: {
        hasNotes: Boolean(form.notes?.trim()),
        hasApproval: Boolean(form.approvers?.users?.length),
        hasNotifications: Boolean(form.notifications?.users?.length),
      },
      notes: form.notes,
      approvers: form.approvers,
      notifications: form.notifications,
    };
  }

  const template = operationType.translationTemplate || '';
  const segments: TranslationSegment[] = [];
  const durationSec = form.durationHours * 3600 + form.durationMinutes * 60 + form.durationSeconds;
  const durationText = buildPreviewDurationText(operationType, durationSec);

  // Parse template tokens and build segments
  const tokens = template.split(/(\{operatorName\}|\{materialName\}|\{parameters\}|\{duration\})/gi);
  for (const token of tokens) {
    const lower = token.toLowerCase();

    if (lower === '{operatorname}') {
      append(segments, operatorName || operationType.operationName || 'Operator');
    } else if (lower === '{materialname}') {
      append(segments, materialName || 'Material');
    } else if (lower === '{parameters}') {
      const paramSegs = buildParameterSegments(operationType, form);
      segments.push(...paramSegs);
    } else if (lower === '{duration}') {
      // Duration is rendered as plain text to avoid empty input-like boxes in preview.
      if (durationText) {
        append(segments, durationText);
      } else {
        removeDanglingDurationPrefix(segments);
      }
    } else {
      append(segments, token);
    }
  }

  const checkpoints = (form.checkpoints?.length ? form.checkpoints : operationType.checkpoints || [])
    .map(mapCheckpoint);

  return {
    nodeId: 'preview-step',
    creationOrder: 0,
    stepNumber: '1',
    type: 'operation',
    operationName: operationType.operationName,
    archetype: operationType.operationType || 'TRANSFORM',
    segments,
    checkpoints,
    icons: {
      hasNotes: Boolean(form.notes?.trim()),
      hasApproval: Boolean(form.approvers?.users?.length),
      hasNotifications: Boolean(form.notifications?.users?.length),
    },
    notes: form.notes,
    approvers: form.approvers,
    notifications: form.notifications,
  };
}

function buildParameterSegments(
  operationType: OperationTypeDefinition,
  form: AddStepFormState,
): TranslationSegment[] {
  const segments: TranslationSegment[] = [];
  const defs = [...(operationType.parameters || [])].sort((a, b) => a.order - b.order);
  let hasAny = false;

  for (const def of defs) {
    if (['duration', 'Duration', 'actionType', 'inputNodes', 'outputNodes'].includes(def.parameterName)) {
      continue;
    }

    const raw = getRawValue(def, form);
    const mandatory = isRequired(def.required);

    // Group parameters
    if (def.fieldType === 'group') {
      const hasChild = def.children?.some((c) => !isEmpty(getRawValue(c, form)));
      const hasMandatoryChild = (def.children || []).some((c) => isRequired(c.required));
      if (!mandatory && !hasMandatoryChild && !hasChild && isEmpty(raw)) continue;

      if (hasAny) append(segments, ', ');
      hasAny = true;
      append(segments, `${def.parameterName}: `);

        let rows = pickGroupRows(raw);

        // CRITICAL: Synthetic empty row for mandatory children
        // If no rows but there ARE child definitions with mandatory children,
        // create one empty row so mandatory children render (even if empty)
        if (rows.length === 0 && def.children && def.children.length > 0 && hasMandatoryChild) {
          rows = [{}];
        }

        rows.forEach((row, idx) => {
        if (idx > 0) append(segments, '; ');
        let rowHasVal = false;
        // Sort children by order field to match protocol generator ordering
        const children = [...(def.children || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        // Filter children: mandatory always show, optional only if has value
        const visibleChildren = children.filter((child) => {
          const rowObj = (row as Record<string, unknown>) || {};
          const childVal = rowObj[child.parameterName] ?? (child.safeKey ? rowObj[child.safeKey] : undefined) ?? rowObj[child.id] ?? getRawValue(child, form);
          return isRequired(child.required) || !isEmpty(childVal);
        });
        for (const child of visibleChildren) {
          const rowObj = (row as Record<string, unknown>) || {};
          const childVal =
            rowObj[child.parameterName] ??
            (child.safeKey ? rowObj[child.safeKey] : undefined) ??
            rowObj[child.id] ??
            getRawValue(child, form);

          const childSegs = buildScalarParamSegments(child, childVal, { disabled: true });
          if (!childSegs) continue;

          if (rowHasVal) append(segments, ', ');
          rowHasVal = true;
          segments.push(...childSegs);
        }
      });
      continue;
    }

    // Gradient program
    if (def.fieldType === 'gradientProgram') {
      const hasReq = (def.children || []).some((c) => isRequired(c.required));
      if (!mandatory && !hasReq && isEmpty(raw)) continue;

      const tbl = buildGradientTable(def, raw, mandatory || hasReq);
      if (!tbl) continue;

      if (hasAny) append(segments, ', ');
      hasAny = true;
      append(segments, `${def.parameterName}: `);
      segments.push(tbl);
      continue;
    }

    // Table
    if (def.fieldType === 'table') {
      if (!mandatory && isEmpty(raw)) continue;

      const tbl = buildDataTable(def, raw, mandatory);
      if (!tbl) continue;

      if (hasAny) append(segments, ', ');
      hasAny = true;
      append(segments, `${def.parameterName}: `);
      segments.push(tbl);
      continue;
    }

    // Dropdown + all scalar params (range, text, number, units, etc.)
    // buildScalarParamSegments handles: mandatory-empty (empty input box),
    // range ("min – max" text), dropdown (plain text, no icon), units, and defaults.
    const scalarSegs = buildScalarParamSegments(def, raw, { disabled: true });
    if (!scalarSegs) continue;

    if (hasAny) append(segments, ', ');
    hasAny = true;
    segments.push(...scalarSegs);
  }

  return segments;
}

function buildGradientTable(def: ParameterDefinition, raw: unknown, mandatory: boolean): TranslationSegment | null {
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const rows = pickRowArray(raw);
  const pumps =
    (typeof obj.totalPumps === 'number' && obj.totalPumps > 0 ? obj.totalPumps : undefined) ??
    (typeof (obj.value as any)?.totalPumps === 'number' && (obj.value as any).totalPumps > 0 ? (obj.value as any).totalPumps : undefined) ??
    def.pumpCount ??
    2;

  // Gradient tables always show (form always has ≥1 row); for non-mandatory: show only if has data or mandatory
  if (!mandatory && rows.length === 0) return null;

  const cols: TableColumn[] = [{ key: 'Time', label: 'Time' }];
  for (let i = 0; i < pumps; i++) {
    const suffix = String.fromCharCode(65 + i);
    cols.push({ key: `Pump${suffix}`, label: `Pump${suffix}` });
  }
  cols.push({ key: 'Conditions', label: 'Conditions' });

  const src = (Array.isArray(rows) && rows.length > 0) ? rows : [{}];
  const tblRows: TableRow[] = src.map((row) => {
    const mapped: TableRow = {};
    for (const col of cols) {
      const rawCell = row[col.key] ?? row[col.label];
      const normalized = getValue(rawCell);
      mapped[col.key] = { value: normalized, fieldType: typeof normalized === 'number' ? 'number' : 'text' };
    }
    return mapped;
  });

  return { kind: 'table', paramName: def.parameterName, columns: cols, rows: tblRows, disabled: true };
}

function buildDataTable(def: ParameterDefinition, raw: unknown, mandatory = false): TranslationSegment | null {
  const rows = pickRowArray(raw);
  const filledRows = rows.filter(rowHasValue);

  if (!mandatory && filledRows.length === 0) return null;

  const cols: TableColumn[] =
    def.tableColumns?.length
      ? normalizeTableColumns(def.tableColumns)
      : filledRows.length > 0
        ? Object.keys(filledRows[0]).map((k) => ({ key: k, label: k }))
        : [];

  if (cols.length === 0) return null;

  // Always show at least one empty row so the table body is never blank
  const srcRows = filledRows.length > 0 ? filledRows : [{}];

  const tblRows: TableRow[] = srcRows.map((row) => {
    const mapped: TableRow = {};
    for (const col of cols) {
      const rawCell = row[col.key] ?? row[col.label];
      const normalized = getValue(rawCell);
      mapped[col.key] = { value: normalized, fieldType: typeof normalized === 'number' ? 'number' : 'text' };
    }
    return mapped;
  });

  return { kind: 'table', paramName: def.parameterName, columns: cols, rows: tblRows, disabled: true };
}

function mapCheckpoint(cp: Checkpoint): CheckpointDef {
  return {
    id: cp.id,
    name: cp.checkpointName,
    fieldType: cp.fieldType,
    mandatory: Boolean(cp.required),
    disabled: true,
    acceptanceMin: cp.rangeMin,
    acceptanceMax: cp.rangeMax,
    allowMultiple: cp.allowMultiple,
    allowSpecialCharacters: cp.allowSpecialCharacters,
    uniqueAcrossWorkspace: cp.uniqueAcrossWorkspace,
    units: cp.units,
    unitSource: cp.unitSource,
    unitCategory: cp.unitCategory,
    linkMultiple: cp.linkMultiple,
    allowedEntityTypes: cp.allowedEntityTypes,
    dropdown: cp.dropdown,
    referenceFile: cp.referenceFile,
    referenceSpreadsheetFile: cp.referenceSpreadsheetFile,
    children: cp.children?.map(mapCheckpoint),
  };
}

// ─── Utilities ─────────────────────────────────────────────────────────────

/**
 * Recursively unwrap parameter wrapper objects.
 * Backend returns: { parameterId, parameterName, value: { parameterId, parameterName, value: {...} } }
 * We need to extract the innermost actual data.
 */
function unwrapParameterWrapper(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const obj = raw as Record<string, unknown>;
  
  // Check if this is a parameter wrapper (has parameterId/parameterName AND value)
  const isWrapper = ('parameterId' in obj || 'parameterName' in obj) && 'value' in obj;
  
  if (isWrapper) {
    // Recursively unwrap the inner value
    return unwrapParameterWrapper(obj.value);
  }
  
  return raw;
}

function getRawValue(def: ParameterDefinition, form: AddStepFormState): unknown {
  let value = form.parameterValues[def.parameterName] ?? form.parameterValues[def.id] ?? (def.safeKey ? form.parameterValues[def.safeKey] : undefined);
  
  // For group parameters, unwrap the parameter wrapper to get actual group data
  if (def.fieldType === 'group') {
    value = unwrapParameterWrapper(value);
  }
  
  return value;
}

// ─── Backward Compat String Renderer ────────────────────────────────────────

export function renderTranslationTemplate(
  operationType: OperationTypeDefinition,
  form: AddStepFormState,
  materialName: string,
  operatorName: string,
): string {
  if (!operationType.translationTemplate) return '';

  if (!operationType.parameters?.length) {
    // Legacy fallback: simple string substitution for tests
    const durationSec = form.durationHours * 3600 + form.durationMinutes * 60 + form.durationSeconds;
    const durationStr = formatDuration(durationSec);
    const paramStr = Object.entries(form.parameterValues)
      .filter(([, v]) => !isEmpty(v))
      .map(([k, v]) => `${k}: ${String(v)}`)
      .join(', ');

    return operationType.translationTemplate
      .replace(/\{operatorName\}/gi, operatorName)
      .replace(/\{materialName\}/gi, materialName)
      .replace(/\{parameters\}/gi, paramStr)
      .replace(/\{duration\}/gi, durationStr);
  }

  // Use new segment-based path
  const preview = buildPreviewStepTranslation(operationType, form, materialName, operatorName);
  return preview.segments
    .map((seg) => {
      if (seg.kind === 'text') return seg.value;
      if (seg.kind === 'field') return String(seg.value ?? '');
      if (seg.kind === 'dropdown') return String(seg.value ?? '');
      return '';
    })
    .join('');
}
